import React, { useContext, useState } from 'react';
import { Button, Badge, Stack, Chip, Select, MenuItem, CircularProgress, Tooltip } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { isEmpty } from 'lodash';
import { usePolicyEditorContext } from './context';
import draftManualHelper from '../../helpers/draftManualHelper';
import { translate as t } from 'hooks/useTranslations';
import { ActionMenu } from 'components/_atoms';
import { useNavigate } from 'react-router-dom';
import { DraftManualDetailContext } from '../context';

type PolicyActionsProps = {};

const PolicyActions: React.FC<PolicyActionsProps> = (props) => {
  const navigate = useNavigate();
  const {
    committedPolicyQuery,
    disableSave,
    gettingLock,
    handleAcquireLockClick,
    setGettingLock,
    handleModifyClick,
    handleModifyPolicyDone,
    handleSave,
    handleUpdateDraftStatus,
    isOpenModifyPolicy,
    policyData,
    policyMode,
    setIsOpenModifyPolicy,
  } = usePolicyEditorContext();
  const { hasPolicyPendingUpdates } = useContext(DraftManualDetailContext);

  const [isUpdatingDraftState, setIsUpdatingDraftState] = useState(false);

  const issuedAt = policyData?.issuedAt;
  const isNeverIssued = issuedAt == null || (typeof issuedAt === 'string' && !issuedAt.trim());
  const isDraftStateChangeAllowed = policyData?.hasPublishableChanges === true || isNeverIssued;
  const selectDisabled = isUpdatingDraftState || !isDraftStateChangeAllowed;

  const setActionsMenuItems = () => {
    const actions = [];
    if (policyData && hasPolicyPendingUpdates(policyData.id)) {
      actions.push({
        key: 'reviewUpdates',
        label: t('draftManuals.reviewUpdates.title'),
        onClick: () => navigate(`./policy:${policyData?.id}/review-updates`),
      });
    }
    return actions;
  };

  const draftStateOptions = ['DRAFT', 'IN_APPROVAL', ...(policyData?.draftState === 'IN_APPROVAL' ? ['APPROVED'] : [])];
  if (!draftStateOptions.includes(policyData?.draftState ?? 'UNKNOWN')) {
    draftStateOptions.push(policyData?.draftState ?? 'UNKNOWN');
  }

  const onHandleModify = async () => {
    try {
      await handleAcquireLockClick();
    } catch (err) {
      setGettingLock(false);
    }
  };

  switch (policyMode) {
    case 'EDITABLE':
      return (
        <Stack
          direction='row'
          spacing={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isUpdatingDraftState ? (
            <CircularProgress />
          ) : (
            <Tooltip
              title={t('draftManuals.draftState.noPublishableChanges')}
              disableHoverListener={isDraftStateChangeAllowed}
              placement='bottom'
            >
              <span>
                <Select
                  id='draft_state'
                  label={false}
                  value={policyData?.draftState ?? ''}
                  size='small'
                  disabled={selectDisabled}
                  onChange={({ target }) => {
                    setIsUpdatingDraftState(true);
                    handleUpdateDraftStatus(target.value, () => setIsUpdatingDraftState(false));
                  }}
                >
                  {draftStateOptions.map((draftState: string) => (
                    <MenuItem
                      key={`draftState_${draftState}`}
                      value={draftState}
                    >
                      {[t('draftManuals.draftState.label'), t(`draftManuals.draftState.${draftState}`)].join(': ')}
                    </MenuItem>
                  ))}
                </Select>
              </span>
            </Tooltip>
          )}
          <Button
            disabled={gettingLock}
            onClick={handleModifyClick}
          >
            {t('draftManuals.actions.editPolicy')}
          </Button>
        </Stack>
      );
    case 'EDITING':
      return (
        <Stack
          direction='row'
          spacing={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {!!policyData?.draftState && (
            <Chip
              label={t(`draftManuals.draftState.${policyData.draftState}`)}
              variant='outlined'
              color='neutral'
              sx={{
                height: 36,
                px: 2,
                '& .MuiChip-label': {
                  fontSize: 16,
                },
              }}
            />
          )}
          {policyMode === 'EDITING' && (
            <>
              {isOpenModifyPolicy ? (
                <Button
                  onClick={handleModifyPolicyDone}
                  sx={{ ml: 2 }}
                  size='small'
                  color='neutral'
                  variant='outlined'
                >
                  {t('policy.sections.closeStructure')}
                </Button>
              ) : (
                !isEmpty(setActionsMenuItems()) && (
                  <ActionMenu
                    id='manual_actions'
                    btnText={t('common.actions')}
                    items={setActionsMenuItems()}
                    btnProps={{
                      size: 'small',
                      variant: 'outlined',
                      color: 'neutral',
                    }}
                  />
                )
              )}
            </>
          )}
          {!isOpenModifyPolicy && (
            <>
              <Button
                onClick={() => handleSave()}
                disabled={disableSave(policyData)}
              >
                {t('draftManuals.actions.savePolicy')}
              </Button>
            </>
          )}
        </Stack>
      );
    case 'LOCKED':
      return (
        <Stack
          direction='row'
          spacing={4}
          sx={{ display: 'flex', alignItems: 'center' }}
          key='locked'
        >
          {!!policyData?.draftState && (
            <Chip
              label={t(`draftManuals.draftState.${policyData.draftState}`)}
              variant='outlined'
              color='neutral'
              sx={{
                height: 36,
                px: 2,
                '& .MuiChip-label': {
                  fontSize: 16,
                },
              }}
            />
          )}
          {policyData?.lock && <>{draftManualHelper.policyLockAvatar(policyData)}</>}
          <Button
            onClick={onHandleModify}
            disabled={gettingLock}
            startIcon={<LockIcon />}
          >
            {t('common.modify')}
          </Button>
        </Stack>
      );
    case 'DISABLED':
      return (
        <Stack
          direction='row'
          spacing={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Added this temporarily to approve disabled policy
          isUpdatingDraftState ? (
            <CircularProgress />
          ) : (
            <Select
              id='draft_state'
              label={false}
              value={policyData?.draftState ?? ''}
              size='small'
              disabled={isUpdatingDraftState}
              onChange={({ target }) => {
                setIsUpdatingDraftState(true);
                handleUpdateDraftStatus(target.value, () => setIsUpdatingDraftState(false));
              }}
            >
              {draftStateOptions.map((draftState: string) => (
                <MenuItem
                  key={`draftState_${draftState}`}
                  value={draftState}
                >
                  {[t('draftManuals.draftState.label'), t(`draftManuals.draftState.${draftState}`)].join(': ')}
                </MenuItem>
              ))}
            </Select>
          )*/}
          <Badge
            variant='inline'
            badgeContent={t('common.disabled')}
            color='warning'
            sx={{ textTransform: 'uppercase' }}
          />
        </Stack>
      );
    default:
      return null;
  }
};

export default PolicyActions;
